<template>
  <div>
    <WdsText secondary class="tw-mb-4">
      {{
        $t(
          'WAADI (Wet allocatie arbeidskrachten door intermediairs) requires all intermediaries supplying personnel in the Netherlands to register in the KvK Business Register.',
        )
      }}
    </WdsText>

    <WdsRadioGroup
      v-model:checked="isRegistered"
      :label="$t('Is your agency registered in the WAADI register?')"
      name="is_registered"
    >
      <WdsRadio :value="true">
        {{ $t('Yes, we are WAADI registered') }}
      </WdsRadio>
      <WdsRadio :value="false">
        {{ $t('No, we are not WAADI registered') }}
      </WdsRadio>
    </WdsRadioGroup>

    <WdsAlert v-if="isRegistered === false" class="tw-mt-4" warning icon>
      {{
        $t(
          'WAADI registration is required for staffing agencies operating in the Netherlands. Please ensure you register to remain compliant.',
        )
      }}
    </WdsAlert>

    <WdsAlert
      v-if="errorMessage && showError"
      class="tw-mt-4"
      closable
      error
      @close="showError = false"
    >
      {{ $t('An error occurred') }}
      <template #description>
        {{ errorMessage }}
      </template>
    </WdsAlert>

    <WdsAlert v-else-if="showSuccess" class="tw-mt-4" closable success @close="showSuccess = false">
      {{ $t('Your WAADI registration status has been saved') }}
    </WdsAlert>

    <div class="tw-mt-4">
      <WdsButton :disabled="loading || isRegistered === null" primary @click="onSave">
        <div class="tw-flex tw-items-center">
          <span>{{ $t('Save') }}</span>
          <WdsSpinner v-show="loading" class="tw-ml-2" indeterminate>
            <WdsIcon name="bam" :size="16" />
          </WdsSpinner>
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsIcon,
  WdsRadio,
  WdsRadioGroup,
  WdsSpinner,
  WdsText,
} from '@worksome/wds-components'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { FormValidation } from '@/utils/formValidation'

export default {
  name: 'NlWaadiRegistrationSettingsForm',
  components: {
    WdsAlert,
    WdsButton,
    WdsIcon,
    WdsRadio,
    WdsRadioGroup,
    WdsSpinner,
    WdsText,
  },
  props: {
    recruiter: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const errors = ref([])
    const internalComplianceData = reactive(props.complianceData)
    const loading = ref(false)
    const showError = ref(false)
    const showSuccess = ref(false)

    return {
      errors,
      internalComplianceData,
      loading,
      showError,
      showSuccess,
    }
  },
  computed: {
    isRegistered: {
      get() {
        return this.internalComplianceData.isRegistered ?? null
      },
      set(value) {
        this.internalComplianceData.isRegistered = value
      },
    },
    errorMessage() {
      return FormValidation.containsErrorMessage(this.errors)
    },
  },
  methods: {
    async onSave() {
      this.loading = true

      const data = JSON.stringify({
        is_registered: this.isRegistered,
      })

      try {
        await this.$apollo.mutate({
          mutation: UpdateComplianceMutation,
          variables: {
            input: {
              name: 'NL_WAADI_REGISTRATION',
              data,
            },
          },
        })

        this.showSuccess = true
        this.showError = false
      } catch ({ graphQLErrors }) {
        this.errors = graphQLErrors
        this.showError = true
        this.showSuccess = false
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
